import React, { useState, useMemo } from "react";
import { Plus, Search, Edit2, Trash2 } from "lucide-react";
import type { Column, TableAction } from "@/components/ui/table";
import { CommonTable } from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import type { DegreeDetail } from "@/interface/masterManagement";
import { useDegreeList, useDeleteDegree } from "./hooks/useDegree";
import DegreeFormModal from "./components/DegreeFormModal";
import type { PaginationResponse } from "@/interface";

const PAGE_SIZE = 10;

export const DegreeManagement: React.FC = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDegree, setSelectedDegree] = useState<DegreeDetail | null>(
    null,
  );

  const { data: response, isLoading } = useDegreeList({
    page: currentPage,
    page_size: PAGE_SIZE,
    search,
  }) as {
    data: {
      data: PaginationResponse<DegreeDetail>;
    };
    isLoading: boolean;
  };

  const deleteMutation = useDeleteDegree();

  const handleEdit = (row: DegreeDetail) => {
    setSelectedDegree(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row: DegreeDetail) => {
    setSelectedDegree(row);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDegree) return;
    try {
      await deleteMutation.mutateAsync(selectedDegree.id);
      setIsDeleteModalOpen(false);
      setSelectedDegree(null);
    } catch {
      // Error handled
    }
  };

  const columns: Column<DegreeDetail>[] = [
    {
      key: "degree_name",
      header: "Degree Name",
      render: (row) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {row.degree_name || row.name}
        </span>
      ),
    },
  ];

  const actions: TableAction<DegreeDetail>[] = useMemo(
    () => [
      {
        label: "Edit",
        icon: () => <Edit2 className="w-4 h-4" />,
        onClick: handleEdit,
      },
      {
        label: "Delete",
        icon: () => <Trash2 className="w-4 h-4" />,
        variant: "danger",
        onClick: handleDeleteClick,
      },
    ],
    [],
  );

  const degreeList = response?.data?.results || [];
  const totalPages = response?.data?.totalPages || 1;
  const totalItems = response?.data?.totalItems;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-6 bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm p-4 sm:p-6 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search degrees..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full h-10 pl-9 pr-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all placeholder:text-slate-400"
            />
          </div>
          <Button
            onClick={() => {
              setSelectedDegree(null);
              setIsFormOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Degree</span>
          </Button>
        </div>

        <CommonTable
          columns={columns}
          data={degreeList}
          actions={actions}
          loading={isLoading}
          emptyMessage="No degrees found."
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            limit={PAGE_SIZE}
            onPageChange={(page) => setCurrentPage(page)}
            entryLabel="degrees"
          />
        )}
      </div>
      <DegreeFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedDegree(null);
        }}
        editData={selectedDegree}
      />

      <ConfirmationDialog
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedDegree(null);
        }}
        title="Delete Degree"
        description={`Are you sure you want to delete degree "${
          selectedDegree?.degree_name || selectedDegree?.name || ""
        }"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default DegreeManagement;
